import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Header from "../components/Header/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsappButton";
import { useTemplateConfig } from "../templates/templateConfigContext";
import { useCategories } from "../components/Header/hooks/useCategories";
import { trackMetric } from "@ecommerce/analytics";

function BarraCategorias() {
    const { config } = useTemplateConfig();
    const { categories, loading } = useCategories();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    if (loading || categories.length === 0) return null;

    const visibles = [...categories]
        .filter((c) => c.sku && c.nombre)
        .sort((a, b) => (a.orden ?? 999) - (b.orden ?? 999))
        .slice(0, 10);

    const activeSku = pathname.startsWith("/categoria/")
        ? pathname.replace("/categoria/", "")
        : pathname === "/productos"
          ? "__todos"
          : null;

    const activeStyle = (sku: string) =>
        activeSku === sku
            ? {
                  color: config.theme.colores.vchMarcaPrincipal,
                  borderBottom: `2px solid ${config.theme.colores.vchMarcaPrincipal}`,
                  fontWeight: 600,
              }
            : {
                  color: config.theme.header.vchTexto,
                  borderBottom: "2px solid transparent",
                  fontWeight: 400,
              };

    const handleCategoryClick = (categorySku: string) => {
        trackMetric("CATEGORY_CLICK", { entitySku: categorySku });
        navigate(`/categoria/${categorySku}`);
    };

    return (
        <nav
            style={{
                background: config.theme.header.vchFondo,
                borderBottom: `1px solid ${config.theme.colores.vchDivider}`,
                overflowX: "auto",
                scrollbarWidth: "none",
            }}
        >
            <div
                style={{
                    maxWidth: config.layout.intContentMaxWidth,
                    margin: "0 auto",
                    padding: "0 32px",
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    whiteSpace: "nowrap",
                }}
            >
                <button
                    type="button"
                    onClick={() => navigate("/productos")}
                    style={{
                        padding: "10px 14px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        fontSize: 13,
                        transition: "color 0.15s, border-color 0.15s",
                        ...activeStyle("__todos"),
                    }}
                >
                    Todo
                </button>
                {visibles.map((category) => (
                    <button
                        key={category.id}
                        type="button"
                        onClick={() => handleCategoryClick(category.sku)}
                        style={{
                            padding: "10px 14px",
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            fontSize: 13,
                            transition: "color 0.15s, border-color 0.15s",
                            ...activeStyle(category.sku),
                        }}
                        onMouseEnter={(e) => {
                            if (activeSku !== category.sku) {
                                (e.currentTarget as HTMLButtonElement).style.color =
                                    config.theme.colores.vchMarcaPrincipal;
                                (e.currentTarget as HTMLButtonElement).style.borderBottomColor =
                                    config.theme.colores.vchMarcaPrincipal;
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (activeSku !== category.sku) {
                                (e.currentTarget as HTMLButtonElement).style.color =
                                    config.theme.header.vchTexto;
                                (e.currentTarget as HTMLButtonElement).style.borderBottomColor =
                                    "transparent";
                            }
                        }}
                    >
                        {category.nombre}
                    </button>
                ))}
            </div>
        </nav>
    );
}

const DefaultLayout: React.FC = () => {
    const { config } = useTemplateConfig();
    const bitBarraCategorias = config.layout.bitBarraCategorias ?? false;

    return (
        <div className="page-surface">
            <Header />

            {bitBarraCategorias && <BarraCategorias />}

            <main style={{ position: "relative", zIndex: 2 }}>
                <Outlet />
            </main>

            <Footer
                companyName={config.identidad.vchNombreTienda}
                year={new Date().getFullYear()}
            />

            <WhatsAppButton />
        </div>
    );
};

export default DefaultLayout;
